import React, { useState } from 'react';
import campaignData from '../data/campaignData_enhanced.json';
import { Card, CardContent } from '../components/ui/Card';
import { FaRocket, FaCheckCircle, FaClock, FaExclamationTriangle, FaTimesCircle } from 'react-icons/fa';

const formatDateString = (dateStr) => {
  if (!dateStr) return '-';
  const [month, day, year] = dateStr.split('/').map(Number);
  const fullYear = year < 100 ? 2000 + year : year;
  const date = new Date(fullYear, month - 1, day);
  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  });
};

const getStatus = (current, target, kpiLabel, assets) => {
  const percent = (current / target) * 100;
  const kpiAssets = assets[kpiLabel] || [];
  const hasAssets = kpiAssets.length > 0;
  const allPublished = hasAssets && kpiAssets.every(a => a.published);

  if (allPublished && current === 0) {
    return { label: 'Pending Launch', color: '#03a9f4', icon: <FaRocket /> };
  }
  if (current === 0) return { label: 'Building', color: '#9e9e9e', icon: <FaClock /> };
  if (percent >= 80) return { label: 'On Track', color: '#4caf50', icon: <FaCheckCircle /> };
  if (percent >= 50) return { label: 'At Risk', color: '#ff9800', icon: <FaExclamationTriangle /> };
  return { label: 'Off Track', color: '#f44336', icon: <FaTimesCircle /> };
};

const CampaignPerformance = () => {
  const kpis = campaignData.kpis;
  const assets = campaignData.assets;
  const [activeKPIIndex, setActiveKPIIndex] = useState('overall');
  const [filter, setFilter] = useState('all');

  const filteredAssets = (label) => {
    const allAssets = assets[label] || [];
    if (filter === 'all') return allAssets;
    return allAssets.filter((item) => item.published === (filter === 'published'));
  };

  const isOverall = activeKPIIndex === 'overall';

  const renderAssetTable = (assetList) => (
    <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '1rem' }}>
      <thead>
        <tr style={{ backgroundColor: '#f4f4f4', textAlign: 'left' }}>
          <th style={{ padding: '0.5rem' }}>Status</th>
          <th style={{ padding: '0.5rem' }}>Name</th>
          <th style={{ padding: '0.5rem' }}>Launch Date</th>
        </tr>
      </thead>
      <tbody>
        {assetList.map((asset, idx) => (
          <tr key={idx} title={asset.notes} style={{ cursor: asset.url ? 'pointer' : 'default' }} onClick={() => asset.url && window.open(asset.url, '_blank') }>
            <td style={{ padding: '0.5rem' }}>{asset.published ? '✅' : '❌'}</td>
            <td style={{ padding: '0.5rem', color: asset.url ? '#1a0dab' : '#000', textDecoration: asset.url ? 'underline' : 'none' }}>{asset.title}</td>
            <td style={{ padding: '0.5rem' }}>{asset.publishWeek ? formatDateString(asset.publishWeek) : '-'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  return (
    <div style={{ padding: '2rem', fontFamily: 'sans-serif' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', padding: '1rem', backgroundColor: '#fff', borderRadius: '10px', boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)' }}>
        <div style={{ display: 'flex', gap: '2rem', alignItems: 'center' }}>
          {[{ label: 'Overall', value: 'overall' }, ...kpis.map((k, i) => ({ label: k.label, value: i }))].map((tab, idx) => (
            <div key={idx} style={{ textAlign: 'center', cursor: 'pointer' }} onClick={() => setActiveKPIIndex(tab.value)}>
              <div style={{
                fontWeight: activeKPIIndex === tab.value ? 'bold' : 'normal',
                color: activeKPIIndex === tab.value ? '#ea7400' : '#444',
                marginBottom: '0.25rem'
              }}>{tab.label}</div>
              {activeKPIIndex === tab.value && <div style={{ height: '4px', backgroundColor: '#ea7400', borderRadius: '2px' }} />}
            </div>
          ))}
        </div>
        <div>
          <label htmlFor="filter">Filter:</label>{' '}
          <select id="filter" value={filter} onChange={(e) => setFilter(e.target.value)} style={{ padding: '0.5rem', borderRadius: '6px' }}>
            <option value="all">All</option>
            <option value="published">✅ Published</option>
            <option value="unpublished">❌ Unpublished</option>
          </select>
        </div>
      </div>

      <div>
        <Card>
          <CardContent>
            {isOverall ? (
              <>
                <h3>Overall Campaign Performance</h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1.5rem', marginTop: '1rem' }}>
                  {kpis.map((kpi, idx) => {
                    const percent = Math.round((kpi.current / kpi.target) * 100);
                    const status = getStatus(kpi.current, kpi.target, kpi.label, assets);
                    const kpiAssets = filteredAssets(kpi.label);
                    const publishedCount = (assets[kpi.label] || []).filter(a => a.published).length;
                    const total = (assets[kpi.label] || []).length;

                    return (
                      <div key={idx} style={{ border: '1px solid #ddd', borderRadius: '8px', padding: '1rem' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <h4>{kpi.label}</h4>
                          <span style={{ fontSize: '0.85rem', background: '#eee', padding: '0.25rem 0.5rem', borderRadius: '12px' }}>{publishedCount}/{total} Published</span>
                        </div>

                        <div style={{ background: '#f0f0f0', borderRadius: '6px', overflow: 'hidden', marginBottom: '0.5rem' }}>
                          <div style={{ width: `${percent}%`, background: status.color, color: '#fff', padding: '0.25rem 0.5rem' }}>{percent}%</div>
                        </div>
                        <p><strong>Status:</strong> <span style={{ color: status.color, display: 'flex', alignItems: 'center', gap: '0.4rem' }}>{status.icon} {status.label}</span></p>

                        <strong>Tracked Content:</strong>
                        {renderAssetTable(kpiAssets)}
                      </div>
                    );
                  })}
                </div>
              </>
            ) : (
              (() => {
                const kpi = kpis[activeKPIIndex];
                const current = kpi.current;
                const target = kpi.target;
                const status = getStatus(current, target, kpi.label, assets);
                return (
                  <>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
                      <div>
                        <h2 style={{ margin: 0 }}>{kpi.label} KPI</h2>
                        <p style={{ marginTop: '0.25rem', color: '#666' }}>{kpi.objective}</p>
                        <p style={{ marginTop: '0.5rem' }}>
                          <strong>Target:</strong> {kpi.target} &nbsp;&nbsp;
                          <strong>Current:</strong> {kpi.current}
                        </p>
                      </div>
                      <div>
                        <p style={{ margin: 0 }}>
                          <strong>Status:</strong>{' '}
                          <span style={{ color: status.color, display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}>
                            {status.icon} {status.label}
                          </span>
                        </p>
                      </div>
                    </div>

                    <div style={{ marginBottom: '1rem' }}>
                      <div style={{ background: '#eee', borderRadius: '6px', overflow: 'hidden' }}>
                        <div style={{
                          width: `${Math.round((current / target) * 100)}%`,
                          background: status.color,
                          color: '#fff',
                          padding: '0.25rem 0.5rem'
                        }}>
                          {Math.round((current / target) * 100)}%
                        </div>
                      </div>
                    </div>

                    <h4 style={{ marginTop: '1.5rem' }}>Tracked Content</h4>
                    {renderAssetTable(filteredAssets(kpi.label))}
                  </>
                );
              })()
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CampaignPerformance;